/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Divider, Form, message, Modal, Select, Skeleton } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import BadRequestError from '~lib/badRequestError';
import {
  convertSystemModel,
  getAirlineSystemModels,
  getSystemModelProfile,
} from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import normalizeAircards from '~util/normalizeAircards';
import AircardForm, { checkDuplicateAircardValues } from './components/AircardForm';

const SystemModelConversionForm = Form.create({ name: 'provision' })(
  ({ airlineCode, disabled, onInvalidScenario, prevSystemModel, form }) => {
    const [systemModels, setSystemModels] = useState();
    const [selectedSystemModel, setSelectedSystemModel] = useState();
    const [systemModelProfile, setSystemModelProfile] = useState();
    const [loading, setLoading] = useState({
      systemModels: false,
      systemModelProfile: false,
    });

    const { getFieldDecorator, setFieldsValue } = form;
    const { Option } = Select;

    useEffect(() => {
      const getSystemModels = async () => {
        setLoading(prev => ({ ...prev, systemModels: true }));
        try {
          const data = await getAirlineSystemModels(airlineCode);
          if (!!data?.filter) {
            const filteredSystemModels = data.filter(model => model !== prevSystemModel);
            // const filteredSystemModels = ['A', 'B', 'C'].filter(model => model !== prevSystemModel);
            if (!filteredSystemModels?.length) {
              onInvalidScenario();
            } else if (filteredSystemModels?.length === 1) {
              setSelectedSystemModel(filteredSystemModels[0]);
              setFieldsValue({ systemModel: filteredSystemModels[0] });
            }
            setSystemModels(filteredSystemModels);
          }
          setLoading(prev => ({ ...prev, systemModels: false }));
        } catch (e) {
          handleAuthErr(err => {
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
            setLoading(prev => ({ ...prev, systemModels: false }));
          })(e);
        }
      };

      if (!!airlineCode) {
        getSystemModels();
      }
      setSelectedSystemModel();
    }, [airlineCode, onInvalidScenario, prevSystemModel, setFieldsValue]);

    useEffect(() => {
      const updateSystemModelProfile = async () => {
        setLoading(prev => ({ ...prev, systemModelProfile: false }));
        try {
          const data = await getSystemModelProfile(selectedSystemModel);
          setSystemModelProfile(data);
          setLoading(prev => ({ ...prev, systemModelProfile: false }));
        } catch (e) {
          handleAuthErr(err => {
            logger.error(err);
            message.error(constants.API_ERR_MESSAGE);
            setLoading(prev => ({ ...prev, systemModelProfile: false }));
          })(e);
        }
      };

      if (!!selectedSystemModel) updateSystemModelProfile();
    }, [selectedSystemModel]);

    const formStyle = css`
      width: 30rem;
    `;

    return (
      <Form layout="vertical" css={formStyle} hideRequiredMark>
        <Form.Item style={{ marginBottom: '0', paddingBottom: '0' }}>
          <Form.Item label="System Model" style={{ display: 'inline-block' }}>
            {getFieldDecorator('systemModel', {
              rules: [{ required: true, message: 'A system model must be selected' }],
            })(
              <Select
                disabled={systemModels?.length === 1 || !systemModels?.length || disabled}
                loading={loading.systemModels}
                onSelect={value => setSelectedSystemModel(value)}
                placeholder="Model"
                size="large"
                style={{ width: 150 }}
              >
                {systemModels?.map &&
                  systemModels.map(systemModel => (
                    <Option key={systemModel} value={systemModel}>
                      {systemModel}
                    </Option>
                  ))}
              </Select>
            )}
          </Form.Item>
        </Form.Item>
        <Divider style={{ margin: '0.6rem 0' }} />
        <br />
        {!!systemModelProfile?.aircards?.length ? (
          systemModelProfile.aircards.map((aircardGroup, index) => {
            const { type, qty } = aircardGroup;
            const FormComponent = AircardForm.Provision[type.toUpperCase()];
            const aircardGroupForms = [];
            for (let i = 0; i < qty; i++) {
              aircardGroupForms.push(
                <FormComponent
                  disabled={disabled}
                  getFieldDecorator={getFieldDecorator}
                  index={i}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${type}${index}-${i}`}
                  type={type}
                />
              );
            }
            return aircardGroupForms;
          })
        ) : (
          <Skeleton paragraph={{ rows: 6 }} />
        )}
      </Form>
    );
  }
);

const SystemModelConversionModal = ({ config, onClose, updateConfig, visible }) => {
  const [isValidScenario, setIsValidScenario] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formRef, setFormRef] = useState(null);

  const airlineCode = config?.airlineCode;
  const prevSystemModel = config?.model;

  // Shrug: https://medium.com/@alef.duarte/using-ant-design-form-inside-a-modal-in-react-stateless-functional-component-634f33357c80
  const saveFormRef = useCallback(node => {
    if (node !== null) setFormRef(node);
  }, []);

  const handleConfirm = () => {
    if (!!formRef?.validateFields && !!formRef?.setFields) {
      formRef.validateFields(async (errors, values) => {
        const duplicateValueErrors = checkDuplicateAircardValues(values?.aircards);
        if (!!duplicateValueErrors) {
          formRef.setFields(duplicateValueErrors);
        } else if (!errors && !!values) {
          setLoading(true);
          try {
            const { systemModel, aircards } = values;
            const normalizedAircards = normalizeAircards(aircards);
            const data = await convertSystemModel(config, systemModel, normalizedAircards);
            setLoading(false);
            updateConfig(data);
            onClose();
          } catch (e) {
            handleAuthErr(err => {
              logger.error(err);
              setLoading(false);
              if (err instanceof BadRequestError) {
                message.error(`Bad Request: ${err.message || 'No message available'}`, 6);
              } else {
                message.error(constants.API_ERR_MESSAGE, 0);
                onClose();
              }
            })(e);
          }
        }
      });
    }
  };

  return (
    <Modal
      cancelButtonProps={{ type: 'link', disabled: loading }}
      closable={false}
      confirmLoading={loading}
      maskClosable={!loading}
      okButtonProps={{ disabled: !isValidScenario }}
      okText="Confirm"
      onCancel={onClose}
      onOk={handleConfirm}
      style={{ display: 'flex', justifyContent: 'center' }}
      title="Convert System Model"
      visible={visible}
    >
      <SystemModelConversionForm
        airlineCode={airlineCode}
        disabled={loading}
        onInvalidScenario={() => setIsValidScenario(false)}
        prevSystemModel={prevSystemModel}
        ref={saveFormRef}
      />
      {!isValidScenario && (
        <p
          className="ant-form-explain"
          css={css`
            color: #f5222d;
            margin: 0.8rem 0 0 0;
          `}
        >
          The {airlineCode} airline does not support any other system models
        </p>
      )}
    </Modal>
  );
};

export default SystemModelConversionModal;
